import { css, keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Copy } from 'react-feather';
import { Link } from 'react-router-dom';

import { copyCompleteText } from '../systemMetadata';

const actionStyle = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  padding: 0;
  border: 1px solid currentColor;
  border-radius: 6px;
  background: transparent;
  color: inherit;
  cursor: pointer;
  text-decoration: none;
  vertical-align: middle;
  transition: background 150ms;
  &:hover {
    background: rgba(127, 127, 127, 0.15);
  }
  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 3px;
  }
  svg {
    display: block;
    width: 15px;
    height: 15px;
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const VersionIconButton = styled.button`
  ${actionStyle}
  border-color: #126981;
  background: #126981;
  color: #fff;
  &:hover {
    background: #0c5368;
  }
  &:focus-visible {
    outline-color: #1693af;
  }
`;
export const VersionIconLink = styled(Link)`
  ${actionStyle}
`;

export type CopyValue = (value: string, label: string) => void;

export const VersionCopyButton: React.FC<{
  label: string;
  value: string;
  copy: CopyValue;
}> = ({ label, value, copy }) => (
  <VersionIconButton
    type="button"
    aria-label={`Copy ${label}`}
    title={`Copy ${label}`}
    onClick={() => copy(value, label)}
  >
    <Copy aria-hidden="true" />
  </VersionIconButton>
);

export function useVersionCopy(): { copy: CopyValue; feedback: string; sequence: number } {
  const [feedback, setFeedback] = useState('');
  const [sequence, setSequence] = useState(0);
  const request = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(
    () => () => {
      request.current += 1;
      clearTimeout(timer.current);
    },
    [],
  );

  const copy = useCallback<CopyValue>((value, label) => {
    const current = ++request.current;
    clearTimeout(timer.current);
    setFeedback('');
    const report = (message: string) => {
      if (current !== request.current) return;
      setFeedback(message);
      setSequence(current);
      timer.current = setTimeout(() => setFeedback(''), 2400);
    };
    void copyCompleteText(value).then(
      () => report(`${label} copied`),
      () => report('Could not copy. Please try again.'),
    );
  }, []);
  return { copy, feedback, sequence };
}

const fadeFeedback = keyframes`
  0%, 82% { opacity: 1; }
  100% { opacity: 0; }
`;

export const VersionCopyNotice = styled.div`
  position: absolute;
  right: 0;
  bottom: calc(100% + 10px);
  width: max-content;
  max-width: min(360px, calc(100vw - 32px));
  font-size: 13px;
  font-weight: 400;
  line-height: 1.4;
  pointer-events: none;
  > span {
    display: block;
    padding: 10px 14px;
    border-radius: 8px;
    background: #183b4b;
    color: #fff;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
    animation: ${fadeFeedback} 2400ms forwards;
  }
  @media (prefers-reduced-motion: reduce) {
    > span {
      animation: none;
    }
  }
`;
